import axios from 'axios';
import { createHttpClient } from '../../../api/http';
import { serviceUrls } from '../../../config/serviceUrls';

export const billingClient = createHttpClient(serviceUrls.gateway);

/**
 * Public catalog base URL.
 * Default to gateway so pricing/catalog uses the same billing instance as authenticated school-admin APIs.
 * Override with VITE_BILLING_PUBLIC_URL when needed (e.g. direct billing-service).
 */
function resolvePublicBillingBaseUrl(): string {
  const explicit = (import.meta.env.VITE_BILLING_PUBLIC_URL as string | undefined)?.trim();
  if (explicit) return explicit;
  return serviceUrls.gateway;
}

/** Unauthenticated client for public billing endpoints (e.g. /pricing). */
const publicBillingClient = axios.create({
  baseURL: resolvePublicBillingBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
});

/** Billing plan from GET /v1/billing/plans */
export interface BillingPlan {
  plan_id: string;
  scope?: string;
  name: string;
  interval: 'month' | 'year';
  amount: number;
  currency: string;
  status: string;
  stripe_product_id?: string;
  stripe_price_id?: string;
  features?: Record<string, boolean>;
  limits?: Record<string, number>;
  description?: string;
  marketing_features?: string[];
  attributes?: Record<string, unknown>;
  statement_descriptor?: string;
  metadata?: Record<string, unknown>;
  prices_history?: unknown[];
  created_at?: string;
  updated_at?: string;
}

/** Request body for POST /v1/billing/plans */
export interface CreatePlanRequest {
  name: string;
  interval: 'month' | 'year';
  amount: number;
  currency: string;
  description?: string;
  marketing_features?: string[];
  attributes?: Record<string, unknown>;
  statement_descriptor?: string;
  metadata?: Record<string, unknown>;
  features?: Record<string, boolean>;
  limits?: Record<string, number>;
}

export interface ListPlansParams {
  limit?: number;
  skip?: number;
}

/** @deprecated Legacy Stripe catalog — use listSubscriptionPlanCatalog + listSchoolSubscriptions. */
/** GET /v1/billing/plans */
export const listPlans = async (params?: ListPlansParams): Promise<BillingPlan[]> => {
  const response = await billingClient.get<BillingPlan[]>('/v1/billing/plans', {
    params: { limit: params?.limit ?? 100, skip: params?.skip },
  });
  const data = response.data;
  return Array.isArray(data) ? data : [];
};

/** POST /v1/billing/plans */
export const createPlan = async (data: CreatePlanRequest): Promise<BillingPlan> => {
  const response = await billingClient.post<BillingPlan>('/v1/billing/plans', data);
  return response.data;
};

/** POST /v1/billing/plans/:planId/activate */
export const activatePlan = async (planId: string): Promise<BillingPlan> => {
  const response = await billingClient.post<BillingPlan>(`/v1/billing/plans/${planId}/activate`);
  return response.data;
};

/** POST /v1/billing/plans/:planId/deactivate */
export const deactivatePlan = async (planId: string): Promise<BillingPlan> => {
  const response = await billingClient.post<BillingPlan>(`/v1/billing/plans/${planId}/deactivate`);
  return response.data;
};

/** @deprecated Legacy Stripe checkout catalog — use listSubscriptionPlanCatalog on My Plan. */
/** GET /v1/billing/plans/public — list plans available for a tenant (e.g. school admin). Send X-Tenant-Id. */
export const getPublicPlans = async (tenantId: string): Promise<BillingPlan[]> => {
  const response = await billingClient.get<BillingPlan[]>('/v1/billing/plans/public', {
    headers: { 'X-Tenant-Id': tenantId },
  });
  const data = response.data;
  return Array.isArray(data) ? data : [];
};

/** Tenant subscription from GET /v1/billing/tenant-subscription/me */
export type TenantSubscription = {
  tenant_id: string;
  plan_id: string;
  status: 'active' | 'trialing' | 'canceled' | 'past_due' | 'incomplete' | string;
  current_period_start: string;
  current_period_end: string;
  entitlements_snapshot?: {
    plan_name?: string;
    interval?: 'month' | 'year' | string;
    amount?: number;
    currency?: string;
    features?: Record<string, unknown>;
    limits?: Record<string, unknown>;
  };
  has_premium_access?: boolean;
  grace_until?: string | null;
};

type ApiErrorEnvelope = { error?: { code?: string; message?: string } };

/** @deprecated For plan UX use getPrimarySchoolSubscription / getSchoolSubscriptionSummary. Stripe portal only. */
/** GET /v1/billing/tenant-subscription/me — returns null when NOT_FOUND (no active subscription). */
export async function getMyTenantSubscription(tenantId: string): Promise<TenantSubscription | null> {
  try {
    const res = await billingClient.get<TenantSubscription>('/v1/billing/tenant-subscription/me', {
      headers: { 'X-Tenant-Id': tenantId },
    });
    return res.data;
  } catch (e: unknown) {
    const data: ApiErrorEnvelope | undefined =
      (e as { response?: { data?: ApiErrorEnvelope } })?.response?.data;
    const code = data?.error?.code;
    if (code === 'NOT_FOUND') return null;
    throw e;
  }
}

/** POST /v1/billing/portal/session — Stripe Customer Billing Portal (invoices, payment method). SCHOOL_ADMIN + X-Tenant-Id. */
export async function createBillingPortalSession(
  tenantId: string,
  body?: { return_url?: string }
): Promise<{ portal_url: string }> {
  const res = await billingClient.post<{ portal_url: string }>('/v1/billing/portal/session', body ?? {}, {
    headers: { 'X-Tenant-Id': tenantId },
  });
  return res.data;
}

/** Request for POST /v1/billing/checkout/tenant-subscription */
export interface CheckoutTenantRequest {
  plan_id: string;
  success_url: string;
  cancel_url: string;
  coupon_code?: string;
  idempotency_key?: string;
}

/** Response from POST /v1/billing/checkout/tenant-subscription */
export interface CheckoutTenantResponse {
  checkout_url: string;
  session_id: string;
}

/** POST /v1/billing/checkout/tenant-subscription — create Stripe Checkout Session for tenant subscription. Redirect user to checkout_url. */
export const createCheckoutSession = async (
  tenantId: string,
  body: CheckoutTenantRequest,
  idempotencyKey?: string
): Promise<CheckoutTenantResponse> => {
  const headers: Record<string, string> = { 'X-Tenant-Id': tenantId };
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  const payload: CheckoutTenantRequest = {
    plan_id: body.plan_id,
    success_url: body.success_url,
    cancel_url: body.cancel_url,
    ...(body.coupon_code && { coupon_code: body.coupon_code }),
    ...(body.idempotency_key && { idempotency_key: body.idempotency_key }),
  };
  const response = await billingClient.post<CheckoutTenantResponse>(
    '/v1/billing/checkout/tenant-subscription',
    payload,
    { headers }
  );
  return response.data;
};

// ——— Coupons ———

export type Coupon = {
  coupon_id: string;
  code: string;
  type: 'percent' | 'amount';
  value: number;
  currency?: string | null;
  duration: 'once' | 'repeating' | 'forever';
  duration_in_months?: number | null;
  max_redemptions?: number | null;
  redeem_by?: number | null;
  status: 'active' | 'inactive';
  stripe_coupon_id?: string | null;
  stripe_promo_code_id?: string | null;
  created_at?: string;
};

export type CreateCouponRequest = {
  code: string;
  type: 'percent' | 'amount';
  value: number;
  duration: 'once' | 'repeating' | 'forever';
  duration_in_months?: number;
  currency?: string;
  max_redemptions?: number;
  redeem_by?: number;
};

export async function listCoupons(params?: {
  status?: 'active' | 'inactive';
  limit?: number;
  skip?: number;
}): Promise<Coupon[]> {
  const query: Record<string, string> = {};
  if (params?.status) query.status = params.status;
  if (typeof params?.limit === 'number') query.limit = String(params.limit);
  if (typeof params?.skip === 'number') query.skip = String(params.skip);
  const response = await billingClient.get<Coupon[]>('/v1/billing/coupons', { params: query });
  const data = response.data;
  return Array.isArray(data) ? data : [];
}

export async function createCoupon(body: CreateCouponRequest): Promise<Coupon> {
  const response = await billingClient.post<Coupon>('/v1/billing/coupons', body);
  return response.data;
}

export async function disableCoupon(couponId: string): Promise<Coupon> {
  const response = await billingClient.patch<Coupon>(`/v1/billing/coupons/${couponId}/disable`, {});
  return response.data;
}

/** Response from GET /v1/billing/coupons/validate?code=... (backend must implement this endpoint). */
export type CouponValidateResponse =
  | {
      valid: true;
      code: string;
      status: 'active';
      type: 'percent' | 'amount';
      value: number;
      currency?: string | null;
      duration: 'once' | 'repeating' | 'forever';
      duration_in_months?: number | null;
      max_redemptions?: number | null;
      redeem_by?: number | null;
      message?: string;
    }
  | {
      valid: false;
      code: string;
      message: string;
    };

/** GET /v1/billing/coupons/validate?code=CODE — validate coupon before checkout. */
export async function validateCouponCode(code: string): Promise<CouponValidateResponse> {
  const normalized = code.trim().toUpperCase();
  const response = await billingClient.get<CouponValidateResponse>('/v1/billing/coupons/validate', {
    params: { code: normalized },
  });
  return response.data;
}

// ——— Subscription plan catalog & school subscriptions (DEV_TEAM, Phase 1) ———

export type SubscriptionPlanCatalogRow = {
  id: string;
  plan_key: string;
  plan_name: string;
  description?: string;
  monthly_price_usd: number;
  annual_price_usd: number;
  max_students: number;
  max_teachers: number;
  max_parents: number;
  live_daily_minutes: number;
  live_monthly_minutes: number;
  max_session_minutes?: number;
  idle_timeout_seconds?: number;
  ai_tutor_enabled: boolean;
  communication_enabled: boolean;
  scenario_enabled: boolean;
  picture_talk_enabled: boolean;
  story_enabled: boolean;
  grammar_enabled: boolean;
  analytics_enabled: boolean;
  parent_portal_enabled: boolean;
  transcript_retention_days: number;
  analytics_retention_days: number;
  support_tier: string;
  is_active: boolean;
  display_order: number;
};

export type EnterpriseOverrides = {
  modules?: string[];
  custom_modules?: string[];
  student_limit?: number;
  teacher_limit?: number;
  parent_limit?: number;
  daily_live_minutes?: number;
  monthly_live_minutes?: number;
  custom_monthly_price?: number;
  custom_yearly_price?: number;
  custom_price_usd?: number;
  trial_days?: number;
  bypass_stripe?: boolean;
};

export type SchoolSubscriptionRow = {
  id: string;
  school_id: string;
  parent_tenant_id: string;
  plan_key: string;
  status: string;
  start_date?: string | null;
  end_date?: string | null;
  assignment_source: string;
  stripe_subscription_id?: string | null;
  sync_status?: string | null;
  last_sync_at?: string | null;
  last_sync_error?: string | null;
  last_entitlement_sync_ok?: boolean | null;
  last_entitlement_sync_error?: string | null;
  enterprise_overrides?: EnterpriseOverrides | null;
};

export type VerifySubscriptionSyncRow = {
  quota_sync_ok: boolean;
  school_flags_ok: boolean;
  grade_allocations_ok: boolean;
  mismatches: string[];
};

export type SchoolSubscriptionSummaryRow = {
  school_id: string;
  school_name: string;
  parent_tenant_id?: string;
  school_created_at?: string | null;
  subscription_id?: string | null;
  plan_key: string;
  plan_name: string;
  status: string;
  start_date?: string | null;
  end_date?: string | null;
  assignment_source: string;
  billing_cycle?: string | null;
  stripe_status?: string | null;
  current_period_end?: string | null;
  stripe_customer_id?: string | null;
  subscription_created_at?: string | null;
  max_students: number;
  max_teachers: number;
  max_parents: number;
  used_students: number;
  used_teachers: number;
  used_parents: number;
  live_daily_minutes: number;
  live_monthly_minutes: number;
  max_session_minutes?: number;
  idle_timeout_seconds?: number;
  ai_tutor_enabled?: boolean;
  communication_enabled?: boolean;
  scenario_enabled?: boolean;
  picture_talk_enabled?: boolean;
  default_daily_allocations?: Record<string, number> | null;
  default_monthly_allocations?: Record<string, number> | null;
  enabled_modules: string[];
  support_tier?: string;
  transcript_retention_days?: number;
  organization_contact?: string;
  billing_contact_email?: string;
  trial_days_remaining?: number | null;
  trial_started_at?: string | null;
  trial_expires_at?: string | null;
  last_sync_at?: string | null;
  sync_status: string;
  last_sync_error?: string | null;
  custom_monthly_price?: number | null;
  custom_yearly_price?: number | null;
  is_enterprise_custom?: boolean;
  monthly_price_usd?: number | null;
  annual_price_usd?: number | null;
};

export type SeatUtilizationRow = {
  school_id: string;
  max_students: number;
  max_teachers: number;
  max_parents: number;
  used_students: number;
  used_teachers: number;
  used_parents: number;
  student_utilization: number;
  teacher_utilization: number;
  parent_utilization: number;
};

export type SchoolSeatCapacityRow = {
  school_id: string;
  subscription_active: boolean;
  plan_key?: string;
  max_students: number;
  max_teachers: number;
  max_parents: number;
  used_students?: number | null;
  used_teachers?: number | null;
  used_parents?: number | null;
  student_utilization?: number | null;
  teacher_utilization?: number | null;
  parent_utilization?: number | null;
};

export async function listSubscriptionPlanCatalog(activeOnly = false): Promise<SubscriptionPlanCatalogRow[]> {
  const res = await billingClient.get<SubscriptionPlanCatalogRow[]>('/v1/billing/subscription-plan-catalog', {
    params: { active_only: activeOnly },
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function listSchoolSubscriptions(params?: {
  school_id?: string;
  skip?: number;
  limit?: number;
}): Promise<SchoolSubscriptionRow[]> {
  const res = await billingClient.get<SchoolSubscriptionRow[]>('/v1/billing/school-subscriptions', {
    params: {
      school_id: params?.school_id,
      skip: params?.skip ?? 0,
      limit: params?.limit ?? 100,
    },
  });
  return Array.isArray(res.data) ? res.data : [];
}

const SCHOOL_SUB_STATUS_RANK: Record<string, number> = {
  active: 0,
  trial: 1,
  suspended: 2,
  expired: 3,
  cancelled: 4,
};

/** Primary school subscription row for a tenant (from GET /v1/billing/school-subscriptions). */
export async function getPrimarySchoolSubscription(schoolId: string): Promise<SchoolSubscriptionRow | null> {
  const rows = await listSchoolSubscriptions({ school_id: schoolId, limit: 20 });
  if (!rows.length) return null;
  const preferred = rows.filter((r) => {
    const s = (r.status || '').toLowerCase();
    return s === 'active' || s === 'trial' || s === 'suspended';
  });
  const pool = preferred.length ? preferred : rows;
  return [...pool].sort((a, b) => {
    const ra = SCHOOL_SUB_STATUS_RANK[(a.status || '').toLowerCase()] ?? 99;
    const rb = SCHOOL_SUB_STATUS_RANK[(b.status || '').toLowerCase()] ?? 99;
    return ra - rb;
  })[0];
}

export async function createSchoolSubscription(body: {
  school_id: string;
  parent_tenant_id: string;
  plan_key: string;
  status: 'trial' | 'active' | 'suspended' | 'expired' | 'cancelled';
  start_date?: string;
  end_date?: string;
  assignment_source: 'stripe' | 'manual';
  stripe_subscription_id?: string;
  enterprise_overrides?: EnterpriseOverrides;
}): Promise<SchoolSubscriptionRow> {
  const res = await billingClient.post<SchoolSubscriptionRow>('/v1/billing/school-subscriptions', body);
  return res.data;
}

export async function patchSchoolSubscription(
  subscriptionId: string,
  body: Partial<{
    status: 'trial' | 'active' | 'suspended' | 'expired' | 'cancelled';
    start_date: string;
    end_date: string;
    plan_key: string;
    stripe_subscription_id: string;
    enterprise_overrides: EnterpriseOverrides;
  }>
): Promise<SchoolSubscriptionRow> {
  const res = await billingClient.patch<SchoolSubscriptionRow>(
    `/v1/billing/school-subscriptions/${subscriptionId}`,
    body
  );
  return res.data;
}

export async function resyncSchoolSubscriptionEntitlements(subscriptionId: string): Promise<{ ok: boolean; error: string | null }> {
  const res = await billingClient.post<{ ok: boolean; error: string | null }>(
    `/v1/billing/school-subscriptions/${subscriptionId}/sync-entitlements`,
    {}
  );
  return res.data;
}

export async function getSeatUtilization(schoolId: string): Promise<SeatUtilizationRow> {
  const res = await billingClient.get<SeatUtilizationRow>(
    `/v1/billing/school-subscriptions/by-school/${encodeURIComponent(schoolId)}/seat-utilization`
  );
  return res.data;
}

export async function getSchoolSeatCapacity(
  schoolId: string,
  opts?: { includeUtilization?: boolean }
): Promise<SchoolSeatCapacityRow> {
  const res = await billingClient.get<SchoolSeatCapacityRow>(
    `/v1/billing/school-subscriptions/by-school/${encodeURIComponent(schoolId)}/seat-capacity`,
    { params: { include_utilization: opts?.includeUtilization ? 'true' : undefined } }
  );
  return res.data;
}

export async function verifySchoolSubscriptionSync(subscriptionId: string): Promise<VerifySubscriptionSyncRow> {
  const res = await billingClient.get<VerifySubscriptionSyncRow>(
    `/v1/billing/school-subscriptions/${encodeURIComponent(subscriptionId)}/verify-sync`
  );
  return res.data;
}

export async function getSchoolSubscriptionSummary(schoolId: string): Promise<SchoolSubscriptionSummaryRow> {
  const res = await billingClient.get<SchoolSubscriptionSummaryRow>(
    `/v1/billing/school-subscriptions/by-school/${encodeURIComponent(schoolId)}/summary`
  );
  return res.data;
}

export type PublicPlanCatalogRow = {
  plan_key: string;
  plan_name: string;
  description: string;
  monthly_price_usd: number;
  annual_price_usd: number;
  max_students: number;
  max_teachers: number;
  max_parents: number;
  live_daily_minutes: number;
  live_monthly_minutes: number;
  max_session_minutes?: number;
  idle_timeout_seconds?: number;
  ai_tutor_enabled: boolean;
  communication_enabled: boolean;
  scenario_enabled: boolean;
  picture_talk_enabled: boolean;
  story_enabled: boolean;
  grammar_enabled: boolean;
  analytics_enabled: boolean;
  parent_portal_enabled: boolean;
  support_tier: string;
  display_order: number;
  available_cycles: string[];
  stripe_checkout_enabled: boolean;
};

const PUBLIC_CATALOG_PATHS = [
  '/v1/billing/marketing/plan-catalog',
  '/v1/billing/subscription-plan-catalog/public',
] as const;

export async function getPublicSubscriptionPlanCatalog(): Promise<PublicPlanCatalogRow[]> {
  let lastError: unknown;
  for (const path of PUBLIC_CATALOG_PATHS) {
    try {
      const res = await publicBillingClient.get<PublicPlanCatalogRow[]>(path);
      return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
      lastError = e;
      const status = axios.isAxiosError(e) ? e.response?.status : undefined;
      if (status === 404 || status === 405) {
        continue;
      }
      throw e;
    }
  }
  throw lastError;
}

export async function createSchoolCheckoutSession(body: {
  school_id: string;
  plan_key: string;
  billing_cycle: 'monthly' | 'yearly';
  success_url?: string;
  cancel_url?: string;
  coupon_code?: string;
}): Promise<{ checkout_url: string; session_id: string }> {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const res = await billingClient.post<{ checkout_url: string; session_id: string }>(
    '/v1/billing/checkout/session',
    {
      ...body,
      success_url:
        body.success_url ??
        `${origin}/admin/billing?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: body.cancel_url ?? `${origin}/admin/billing?checkout=cancel`,
    }
  );
  return res.data;
}

export async function getCheckoutConfirmFallbackEnabled(): Promise<boolean> {
  try {
    const res = await billingClient.get<{ enabled: boolean }>(
      '/v1/billing/checkout/confirm-fallback-enabled'
    );
    return Boolean(res.data?.enabled);
  } catch {
    return false;
  }
}

/** Local-dev fallback only — production activation is webhook-driven. */
export async function confirmSchoolCheckoutSession(body: {
  school_id: string;
  session_id: string;
}): Promise<{ ok: boolean; status: string; plan_key: string }> {
  const res = await billingClient.post<{ ok: boolean; status: string; plan_key: string }>(
    '/v1/billing/checkout/confirm',
    body
  );
  return res.data;
}

/** Local-dev fallback only — production activation is webhook-driven. */
export async function confirmPendingSchoolCheckout(
  schoolId: string
): Promise<{ ok: boolean; status: string; plan_key: string }> {
  const res = await billingClient.post<{ ok: boolean; status: string; plan_key: string }>(
    '/v1/billing/checkout/confirm-pending',
    { school_id: schoolId }
  );
  return res.data;
}

export async function createSchoolCustomerPortalSession(body: {
  school_id: string;
  return_url: string;
}): Promise<{ portal_url: string }> {
  const res = await billingClient.post<{ portal_url: string }>('/v1/billing/customer-portal', body);
  return res.data;
}

export async function changeSchoolPlan(body: {
  school_id: string;
  new_plan_key: string;
  billing_cycle?: 'monthly' | 'yearly';
}): Promise<{ ok: boolean; subscription_id: string; plan_key: string; billing_cycle: string }> {
  const res = await billingClient.post('/v1/billing/change-plan', body);
  return res.data;
}

export type BillingInvoiceRow = {
  invoice_id: string;
  number?: string | null;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string;
  created?: string | null;
  pdf_url?: string | null;
  hosted_invoice_url?: string | null;
};

export type SchoolBillingContactRow = {
  school_id: string;
  billing_contact_email: string;
  org_contact_email?: string;
  admin_email?: string;
  profile_billing_email?: string;
  source?: string;
};

export async function getSchoolBillingContact(schoolId: string): Promise<SchoolBillingContactRow> {
  const res = await billingClient.get<SchoolBillingContactRow>('/v1/billing/billing-contact', {
    params: { school_id: schoolId },
  });
  return res.data;
}

export async function updateSchoolBillingContact(body: {
  school_id: string;
  billing_contact_email: string;
}): Promise<SchoolBillingContactRow> {
  const res = await billingClient.patch<SchoolBillingContactRow>('/v1/billing/billing-contact', body);
  return res.data;
}

export async function listSchoolInvoices(schoolId: string): Promise<BillingInvoiceRow[]> {
  const res = await billingClient.get<BillingInvoiceRow[]>('/v1/billing/invoices', {
    params: { school_id: schoolId },
  });
  return Array.isArray(res.data) ? res.data : [];
}

export type SubscriptionConsistencyAlertRow = {
  id: string;
  school_id: string;
  subscription_id?: string | null;
  mismatch_type: string;
  details: Record<string, unknown>;
  severity: string;
  detected_at?: string | null;
  resolved: boolean;
  resolved_at?: string | null;
  resolved_by?: string | null;
  category?: string | null;
  acknowledged?: boolean;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
};

export async function listSubscriptionConsistencyAlerts(params?: {
  resolved?: boolean;
  school_id?: string;
  category?: string;
  skip?: number;
  limit?: number;
}): Promise<SubscriptionConsistencyAlertRow[]> {
  const res = await billingClient.get<SubscriptionConsistencyAlertRow[]>(
    '/v1/billing/subscription-consistency-alerts',
    {
      params: {
        resolved: params?.resolved,
        school_id: params?.school_id,
        category: params?.category,
        skip: params?.skip ?? 0,
        limit: params?.limit ?? 200,
      },
    }
  );
  return Array.isArray(res.data) ? res.data : [];
}

export async function acknowledgeSubscriptionConsistencyAlert(
  alertId: string
): Promise<SubscriptionConsistencyAlertRow> {
  const res = await billingClient.post<SubscriptionConsistencyAlertRow>(
    `/v1/billing/subscription-consistency-alerts/${encodeURIComponent(alertId)}/acknowledge`,
    {}
  );
  return res.data;
}

export async function resolveSubscriptionConsistencyAlert(
  alertId: string
): Promise<SubscriptionConsistencyAlertRow> {
  const res = await billingClient.post<SubscriptionConsistencyAlertRow>(
    `/v1/billing/subscription-consistency-alerts/${encodeURIComponent(alertId)}/resolve`,
    {}
  );
  return res.data;
}

export async function retrySyncFromSubscriptionAlert(
  alertId: string
): Promise<{ ok: boolean; error: string | null; subscription_id?: string }> {
  const res = await billingClient.post<{ ok: boolean; error: string | null; subscription_id?: string }>(
    `/v1/billing/subscription-consistency-alerts/${encodeURIComponent(alertId)}/retry-sync`,
    {}
  );
  return res.data;
}

export async function runSubscriptionConsistencyCheckNow(): Promise<{ upserts: number }> {
  const res = await billingClient.post<{ upserts: number }>(
    '/v1/billing/subscription-consistency-alerts/run-check-now',
    {}
  );
  return res.data;
}

/** GET /v1/billing/business-snapshot — DEV_TEAM trial/revenue ops counts */
export type BillingBusinessSnapshot = {
  active_trials: number;
  expiring_trials_7d: number;
  expired_trials: number;
  active_paid: number;
  trial_converted_total: number;
  trial_started_total: number;
  trial_to_paid_conversion_pct: number | null;
  payment_failures_open: number;
  renewals_due_open: number;
  billing_alerts_open: number;
};

export async function getBillingBusinessSnapshot(): Promise<BillingBusinessSnapshot> {
  const res = await billingClient.get<BillingBusinessSnapshot>('/v1/billing/business-snapshot');
  return res.data;
}

/** GET /v1/billing/business-dashboard — Phase 3B revenue + AI cost KPIs */
export type SchoolCostRow = {
  school_id: string;
  total_minutes: number;
  total_cost_usd: number;
};

export type RevenueCostPoint = {
  month: string;
  revenue_usd: number;
  ai_cost_usd: number;
  margin_pct: number | null;
};

export type BillingBusinessDashboard = BillingBusinessSnapshot & {
  mrr_usd: number;
  arr_usd: number;
  monthly_ai_cost_usd: number;
  gross_margin_pct: number | null;
  cost_per_student_usd: number | null;
  cost_per_school_usd: number | null;
  top_costly_schools: SchoolCostRow[];
  most_profitable_schools: SchoolCostRow[];
  revenue_vs_cost_series: RevenueCostPoint[];
  margin_trend: RevenueCostPoint[];
};

export async function getBillingBusinessDashboard(): Promise<BillingBusinessDashboard> {
  const res = await billingClient.get<BillingBusinessDashboard>('/v1/billing/business-dashboard');
  return res.data;
}

/** Phase 3C — compliance dashboard */
export type ComplianceDashboard = {
  backup_status: Record<string, unknown> | null;
  last_retention_run: Record<string, unknown> | null;
  privacy_audit: Record<string, unknown> | null;
  recent_exports: { id: string; export_kind?: string; status?: string; created_at?: string | null }[];
  delete_requests: { id: string; school_id?: string; status?: string; created_at?: string | null }[];
};

export async function getComplianceDashboard(): Promise<ComplianceDashboard> {
  const res = await billingClient.get<ComplianceDashboard>('/v1/billing/compliance/dashboard');
  return res.data;
}

export async function runComplianceRetentionNow(): Promise<{ ok: boolean; summary: Record<string, unknown> }> {
  const res = await billingClient.post<{ ok: boolean; summary: Record<string, unknown> }>(
    '/v1/billing/compliance/retention/run-now',
    {}
  );
  return res.data;
}

export async function runComplianceBackupVerifyNow(): Promise<Record<string, unknown>> {
  const res = await billingClient.post<Record<string, unknown>>('/v1/billing/compliance/backup/verify-now', {});
  return res.data;
}

export async function runCompliancePrivacyAuditNow(): Promise<Record<string, unknown>> {
  const res = await billingClient.post<Record<string, unknown>>('/v1/billing/compliance/privacy-audit/run-now', {});
  return res.data;
}

export async function createComplianceExport(body: {
  export_kind: 'transcripts' | 'analytics' | 'audits';
  export_format: 'json' | 'csv';
  school_id?: string;
  skip?: number;
  limit?: number;
  async_job?: boolean;
}): Promise<Record<string, unknown>> {
  const res = await billingClient.post<Record<string, unknown>>('/v1/billing/compliance/exports', body);
  return res.data;
}

export async function requestSchoolDeletion(schoolId: string, reason?: string): Promise<Record<string, unknown>> {
  const res = await billingClient.post<Record<string, unknown>>(
    `/v1/billing/compliance/schools/${encodeURIComponent(schoolId)}/request-deletion`,
    { reason: reason || '' }
  );
  return res.data;
}
